/**
 * A PRESET AVATAR, AS STROKES YOU CAN DRAW ON TOP OF (§ F123, 2026-08-21).
 *
 * Jake: *"When I click an avatar it updates in the 'Draw your own' section, and
 * that is then represented in the actual player space in the top-right. This
 * allows me to pick one to start with and then enhance it."*
 *
 * So a preset stops being a separate KIND of avatar and becomes a STARTING
 * POINT: the glyph is converted into the same polyline strokes a finger
 * produces, dropped into the pad, and from that moment it is an ordinary
 * drawing the player can add to.
 *
 * ── HOW ─────────────────────────────────────────────────────────────────────
 *
 * The glyphs are lucide SVGs. Every drawable element in one is an
 * `SVGGeometryElement`, which the browser can measure and walk:
 * `getTotalLength()` and `getPointAtLength()` turn any curve — a path, a
 * circle, a rounded rect — into points, without this file knowing anything
 * about path syntax. That is the whole trick, and it is why there is no path
 * parser here and no per-icon table.
 *
 * Sampling is by ARC LENGTH, not by segment: a step of ~1.5% of the icon's
 * width keeps a circle round and a straight line cheap, and the result lands
 * well inside the pad's 10KB budget for every one of the forty.
 *
 * It needs a DOM (it renders the component into a detached SVG to measure it),
 * so it is called from an event handler, never during render.
 */

import { renderToStaticMarkup } from "react-dom/server";
import { createElement } from "react";
import type { AvatarGlyph } from "./avatars";
import { DRAW_SPACE, type Drawing, type Stroke } from "../party/drawing";

/** Lucide's own canvas is 24×24. */
const GLYPH_VIEWBOX = 24;

/** How far apart sampled points are, in glyph units (~1.5% of the icon). */
const SAMPLE_STEP = 0.35;

/** The stroke width a seeded glyph is drawn with, in normalized units. */
const SEED_WIDTH = 26;

/** Leave a margin so the shape does not sit on the pad's border. */
const MARGIN = 0.1;

function scale(value: number): number {
  const inset = DRAW_SPACE * (1 - MARGIN * 2);
  return Math.round(DRAW_SPACE * MARGIN + (value / GLYPH_VIEWBOX) * inset);
}

/**
 * Convert one lucide glyph into a drawing.
 *
 * Returns null when there is no DOM (tests, SSR) or the glyph renders nothing
 * measurable — callers treat that as "no seed", never as an error.
 */
export function glyphToDrawing(glyph: AvatarGlyph, color: string): Drawing | null {
  if (typeof document === "undefined") return null;

  let markup: string;
  try {
    markup = renderToStaticMarkup(createElement(glyph, { strokeWidth: 2 }));
  } catch {
    return null;
  }

  const host = document.createElement("div");
  // Off-screen but LAID OUT: `display:none` would make every length zero.
  host.setAttribute(
    "style",
    "position:absolute;left:-9999px;top:0;width:24px;height:24px;pointer-events:none"
  );
  host.innerHTML = markup;
  document.body.appendChild(host);

  try {
    const svg = host.querySelector("svg");
    if (!svg) return null;
    const strokes: Stroke[] = [];

    for (const node of Array.from(svg.querySelectorAll("*"))) {
      const geometry = node as SVGGeometryElement;
      if (typeof geometry.getTotalLength !== "function") continue;
      let length = 0;
      try {
        length = geometry.getTotalLength();
      } catch {
        continue;
      }
      if (!Number.isFinite(length) || length <= 0) continue;

      const steps = Math.max(2, Math.ceil(length / SAMPLE_STEP));
      const pts: number[] = [];
      for (let i = 0; i <= steps; i++) {
        try {
          const point = geometry.getPointAtLength((i / steps) * length);
          pts.push(scale(point.x), scale(point.y));
        } catch {
          break;
        }
      }
      if (pts.length >= 4) strokes.push({ c: color, w: SEED_WIDTH, pts });
    }

    if (strokes.length === 0) return null;
    return { w: DRAW_SPACE, h: DRAW_SPACE, strokes };
  } finally {
    host.remove();
  }
}

export default glyphToDrawing;
